import React, {
  useEffect, useReducer, useRef, useState,
} from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import {
  Button,
  Col,
  DateRangePicker,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Row,
  SelectPicker,
  Stack,
  Table,
} from 'rsuite';
import { Icon } from '@rsuite/icons';
import { FaFileDownload } from 'react-icons/fa';
import { DateRange, RangeType } from 'rsuite/esm/DateRangePicker/types';
import { useStoreActions, useStoreState } from '../../../store/hooks';
import handleError from '../../../api/apiErrorHandle';
import { dayRange } from '../../../services/dateUtils';
import {
  downloadPickupList,
  formatAddress,
  formatSender,
  loadPickupLists,
  toPickupListsRequest,
} from './pickupListService';
import { initialState, reducer } from './pickupListState';
import {
  PickupList,
  PickupListDateType,
  PickupListsRequest,
} from '../../../api/orders/pickuplist/pickupListTypes';

const DATE_TIME_FORMAT = 'yyyy-MM-dd HH:mm';
const DEFAULT_DATE_TYPE = PickupListDateType.SHIP_DATE;

const DATE_TYPES: { value: PickupListDateType, label: string }[] = [
  { value: PickupListDateType.SHIP_DATE, label: 'Shipped' },
  { value: PickupListDateType.SUGGESTED_EXPEDITION, label: 'Suggested expedition' },
];

const ranges: RangeType[] = [
  { label: 'Today', value: () => dayRange(0) },
  { label: 'Yesterday', value: () => dayRange(1) },
  {
    label: 'Last 7 days',
    value: () => [dayjs().subtract(6, 'day').startOf('day').toDate(), dayjs().endOf('day').toDate()],
  },
];

export default function PickupLists(): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [range, setRange] = useState<DateRange | null>(() => dayRange(0));
  const [dateType, setDateType] = useState<PickupListDateType>(DEFAULT_DATE_TYPE);
  const [marketplaceCode, setMarketplaceCode] = useState<string | null>(null);
  const { fetchEshops } = useStoreActions(actions => actions.catalogs);
  const eshops = useStoreState(storeState => storeState.catalogs.eshops);
  const listControllerRef = useRef<AbortController | null>(null);
  const downloadControllerRef = useRef<AbortController | null>(null);

  async function fetchPickupLists(request: PickupListsRequest) {
    listControllerRef.current?.abort();
    const controller = new AbortController();
    listControllerRef.current = controller;

    dispatch({ type: 'lists/loading', payload: request });

    try {
      const pickupLists = await loadPickupLists(request, controller.signal);
      dispatch({ type: 'lists/loaded', payload: pickupLists });
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }

      dispatch({ type: 'lists/failed' });
      handleError(error);
    }
  }

  useEffect(() => {
    fetchEshops().catch(handleError);
  }, [fetchEshops]);

  useEffect(() => {
    fetchPickupLists(toPickupListsRequest(dayRange(0), DEFAULT_DATE_TYPE)).then();

    return () => {
      listControllerRef.current?.abort();
      downloadControllerRef.current?.abort();
    };
  }, []);

  function handleSearch() {
    if (!range) {
      return;
    }

    fetchPickupLists(toPickupListsRequest(range, dateType, marketplaceCode ?? undefined)).then();
  }

  async function submitDownload(pickupList: PickupList, request: PickupListsRequest) {
    const controller = new AbortController();
    downloadControllerRef.current = controller;

    dispatch({ type: 'download/started', payload: pickupList.downloadId });

    try {
      await downloadPickupList(
        { ...request, downloadId: pickupList.downloadId },
        controller.signal,
      );
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }

      if (axios.isAxiosError(error) && error.response?.status === 404) {
        fetchPickupLists(request).then();
      }

      handleError(error);
    } finally {
      if (!controller.signal.aborted) {
        dispatch({ type: 'download/finished' });
      }
    }
  }

  function handleDownload(pickupList: PickupList) {
    const request = state.appliedRequest;
    if (!request) {
      return;
    }

    submitDownload(pickupList, request).then();
  }

  const downloading = state.downloadingId !== null;

  return (
    <Grid fluid className="h100p">
      <Row>
        <Col xs={24}>
          <Stack spacing={6}>
            Date:
            <RadioGroup
              name="dateType"
              inline
              value={dateType}
              onChange={value => setDateType(value as PickupListDateType)}
            >
              {DATE_TYPES.map(type => (
                <Radio key={type.value} value={type.value}>
                  {type.label}
                </Radio>
              ))}
            </RadioGroup>
            between:
            <DateRangePicker
              isoWeek
              cleanable={false}
              format={DATE_TIME_FORMAT}
              size="lg"
              ranges={ranges}
              value={range}
              onChange={setRange}
            />
            Marketplace:
            <SelectPicker
              searchable={false}
              placeholder="All"
              size="lg"
              data={eshops}
              labelKey="name"
              valueKey="code"
              value={marketplaceCode}
              onChange={setMarketplaceCode}
            />
            <Button
              appearance="primary"
              size="lg"
              disabled={!range || downloading}
              loading={state.status === 'loading'}
              onClick={() => handleSearch()}
            >
              Search
            </Button>
          </Stack>
        </Col>
      </Row>
      <Row className="table-container">
        <Col className="mt-20 h100p" xs={24}>
          <Table
            data={state.pickupLists}
            loading={state.status === 'loading'}
            rowKey="downloadId"
            fillHeight
            bordered
          >
            <Table.Column flexGrow={2}>
              <Table.HeaderCell>Place of expedition</Table.HeaderCell>
              <Table.Cell>
                {rowData => formatSender((rowData as PickupList).place)}
              </Table.Cell>
            </Table.Column>
            <Table.Column flexGrow={3}>
              <Table.HeaderCell>Address</Table.HeaderCell>
              <Table.Cell>
                {rowData => formatAddress((rowData as PickupList).place)}
              </Table.Cell>
            </Table.Column>
            <Table.Column flexGrow={2}>
              <Table.HeaderCell>Carrier</Table.HeaderCell>
              <Table.Cell dataKey="carrierIdentifier" />
            </Table.Column>
            <Table.Column width={100} align="right">
              <Table.HeaderCell>Orders</Table.HeaderCell>
              <Table.Cell dataKey="orderCount" />
            </Table.Column>
            <Table.Column width={100} align="right">
              <Table.HeaderCell>Packages</Table.HeaderCell>
              <Table.Cell dataKey="packageCount" />
            </Table.Column>
            <Table.Column width={80} align="center">
              <Table.HeaderCell>Pickup list</Table.HeaderCell>
              <Table.Cell style={{ padding: 8 }}>
                {rowData => (
                  <IconButton
                    size="xs"
                    appearance="subtle"
                    title="Download pickup list"
                    icon={<Icon as={FaFileDownload} />}
                    loading={state.downloadingId === (rowData as PickupList).downloadId}
                    disabled={downloading}
                    onClick={() => handleDownload(rowData as PickupList)}
                  />
                )}
              </Table.Cell>
            </Table.Column>
          </Table>
        </Col>
      </Row>
    </Grid>
  );
}
